import React, { useCallback, useEffect, useState } from 'react';
import { Grid3x3, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/hooks/use-toast';
import { editorUrl } from '@/lib/editorBasePath';
import CrosswordDialog, { CrosswordItem } from './CrosswordDialog';
import { generateCrossword } from '@/lib/crosswordGenerator';
import { buildCrosswordElements } from '@/lib/crosswordToElements';
import { YearbookElement } from '@/types/yearbook';

interface Props {
  onAddElements: (elements: YearbookElement[]) => void;
}

const CrosswordPanel: React.FC<Props> = ({ onAddElements }) => {
  const [items, setItems] = useState<CrosswordItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<CrosswordItem | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<CrosswordItem | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    fetch(editorUrl('/load_crosswords'), { credentials: 'include' })
      .then((res) => res.json())
      .then((data: CrosswordItem[]) => {
        setItems(Array.isArray(data) ? data : []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSaved = (saved: CrosswordItem) => {
    setItems((prev) => {
      const idx = prev.findIndex((p) => p.id === saved.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = saved;
        return next;
      }
      return [saved, ...prev];
    });
  };

  const handleInsert = (item: CrosswordItem) => {
    const grid = generateCrossword(item.words);
    if (grid.cells.length === 0) {
      toast({ title: 'Grille vide', description: 'Aucun mot n’a pu être placé.', variant: 'destructive' });
      return;
    }
    const elements = buildCrosswordElements(grid, item.title);
    onAddElements(elements);
    if (grid.unplaced.length > 0) {
      toast({
        title: 'Mots non placés',
        description: `${grid.unplaced.length} mot(s) n’ont pas pu être insérés : ${grid.unplaced.map((u) => u.word).join(', ')}`,
      });
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    const id = confirmDelete.id;
    setDeletingId(id);
    try {
      const res = await fetch(editorUrl(`/delete_crosswords?cross_id=${id}`), {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (data?.result === false) {
        toast({ title: 'Suppression impossible', description: data?.message || 'Erreur', variant: 'destructive' });
      } else {
        setItems((prev) => prev.filter((p) => p.id !== id));
      }
    } catch {
      toast({ title: 'Erreur réseau', description: 'Suppression impossible', variant: 'destructive' });
    } finally {
      setDeletingId(null);
      setConfirmDelete(null);
    }
  };

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium text-editor-sidebar-foreground text-center flex items-center justify-center gap-1.5">
        <Grid3x3 size={15} />
        Mots croisés
      </h3>

      <Button
        size="sm"
        className="w-full bg-primary-gradient text-white hover:opacity-90"
        onClick={() => { setEditTarget(null); setDialogOpen(true); }}
      >
        <Plus size={14} />
        Nouvelle grille
      </Button>

      {loading && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="animate-spin text-muted-foreground" size={20} />
        </div>
      )}

      {!loading && items.length === 0 && (
        <p className="text-xs text-muted-foreground text-center">Aucune grille pour le moment.</p>
      )}

      {!loading && items.length > 0 && (
        <div className="space-y-2">
          {items.map((it) => (
            <div
              key={it.id}
              className="group relative rounded-md border border-editor-sidebar-border bg-editor-sidebar-hover p-2 hover:border-editor-sidebar-active transition-colors"
            >
              <button
                type="button"
                onClick={() => handleInsert(it)}
                className="w-full text-left"
                title="Insérer dans la page"
              >
                <div className="text-xs font-medium text-editor-sidebar-foreground truncate pr-12">
                  {it.title || 'Sans titre'}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {it.words.length} mot{it.words.length > 1 ? 's' : ''}
                </div>
              </button>

              <div className="absolute top-1 right-1 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={(e) => { e.stopPropagation(); setEditTarget(it); setDialogOpen(true); }}
                  className="w-5 h-5 rounded bg-editor-sidebar text-editor-sidebar-foreground flex items-center justify-center hover:text-editor-sidebar-active"
                  title="Modifier"
                >
                  <Pencil size={11} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setConfirmDelete(it); }}
                  disabled={deletingId === it.id}
                  className="w-5 h-5 rounded bg-destructive text-destructive-foreground flex items-center justify-center hover:scale-110 transition-transform"
                  title="Supprimer"
                >
                  {deletingId === it.id ? <Loader2 size={10} className="animate-spin" /> : <Trash2 size={11} />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <CrosswordDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editTarget}
        onSaved={handleSaved}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la grille ?</AlertDialogTitle>
            <AlertDialogDescription>
              « {confirmDelete?.title} » sera supprimée définitivement.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CrosswordPanel;
